// tslint:disable:object-literal-sort-keys member-ordering max-line-length variable-name
// http://help.adobe.com/en_US/FlashPlatform/reference/actionscript/3/flash/text/TextFormat.html
// http://help.adobe.com/en_US/FlashPlatform/reference/actionscript/3/flash/text/TextLineMetrics.html
import { TextFieldType } from "./TextFieldType";
import { TextFormat } from "./TextFormat";
import { TextFormatAlign } from "./TextFormatAlign";
import { TextFieldAutoSize } from "./TextFieldAutoSize";
import { GridFitType } from "./GridFitType";
import { AntiAliasType } from "./AntiAliasType";
import { StyleSheet } from "./StyleSheet";
import { TextLineMetrics } from "./TextLineMetrics";
import { ColorTransform } from "../display/ColorTransform";
import { currentBrowserInfo } from "../../createjs-utilities/BrowserInfo";
import { TEXT_FIELDS_LOGGER } from "../../../../../index";
import { getLogger } from "@goodgamestudios/logger";
import { applyFormat, getFontFamily } from "./textFieldHelper/applyFormat";
import unescape = require("lodash.unescape");
import isEqual = require("lodash.isequal");
import { FontRestrictionType, TextDrawingState, RichTextInputToken, GlowType, ShadowType, HTMLLinkFormats } from "./textFieldHelper/dataTypes";
import { Link } from "./textFieldHelper/Link";
import { DEFAULT_PASSIVE_LINK_COLOR, WHITE_TEXT_PASSIVE_LINK_COLOR, DARK_TEXT_PASSIVE_LINK_COLOR, FALLBACK_UNDERLINE_COLOR, FALLBACK_TEXT_SIZE } from "./textFieldHelper/defaults";
import flatten = require("lodash.flatten");

import Event = createjs.Event;
import Shape = createjs.Shape;
import DisplayObject = createjs.DisplayObject;
import Text = createjs.Text;
import Point = createjs.Point;
import Stage = createjs.Stage;
import { InputTextFieldService } from "./InputTextFieldService";
import FocusEvent = createjs.FocusEvent;
import STAGE_MOUSE_DOWN = createjs.MouseEvent.STAGE_MOUSE_DOWN;
import { KeyboardEventDispatcher } from "../KeyboardEventDispatcher";
import TextEvent = createjs.TextEvent;
import { Mouse } from "../ui/Mouse";
import { CSSMouseCursor, MouseCursor } from "../ui/MouseCursor";
import { insertInputFieldElementCSS, HTML_TEXTFIELD_CLASS } from "./textFieldHelper/inputFieldCSS";
import { calculateLatestLayoutType, LayoutParam, LatestLayoutType } from "./textFieldHelper/calculateLatestLayoutType";
import Filter = createjs.Filter;
import GlowFilter = createjs.GlowFilter;
import DropShadowFilter = createjs.DropShadowFilter;
import Container = createjs.Container;
import { navigateToURL } from "../utils/navigateToURL";

const logger = getLogger(TEXT_FIELDS_LOGGER + ".TextField");
const DEBUG_TEXT_DRAWINGS = false;
const DEBUG_LINK_RANGE = false;

insertInputFieldElementCSS();

// increasing counter to generated a unique ID for every TextField
let textFieldCount = 0;

type TextValueType =
    { readonly type: "plain", readonly text: string, readonly inputTokens: RichTextInputToken[] } |
    { readonly type: "html", readonly text: string, readonly inputTokens: RichTextInputToken[] };

// shared between all `TextField` instances, last one wins
let selectionText: string | undefined;
document.addEventListener("copy", function(e) {
    const event = e as any;
    if (selectionText && document.activeElement === document.body && event.clipboardData) {
        event.clipboardData.setData("text/plain", selectionText);
        event.preventDefault();
    }
});

/*
 * The TextField is the "low level" instance reflecting a Flash-like API to the transpiled code.
 *
 * For displaying text including the text/message itself, font, metrics, alignment etc. the createjs.Text is used.
 * It will be added as a child to this extension of createjs.Container.
 *
 * Several attributes are different to the ones in Flash, especially leading vs. lineHeight.
 * When working with measurements and placement of the text (alignments), most necessarily the Methods
 * - createjs.Text.getMeasuredWidth()
 * - createjs.Text.getMeasuredHeight()
 * - createjs.Text.getMeasuredLineHeight()
 * will be interesting to be used. To measure any text or parts of this TextField's text, createjs.Text can be
 * cloned (with all font and measurement specific settings). Afterwards, change the cloned createjs.Text.text attribute.
 *
 * Always keep in mind that the Textfield is only visually updated / initialized completely when it has been
 * 1) added to the stage (TextField.onAddedToStage())
 * 2) has been drawn (TextField.redraw() was called at least once).
 * Only at that moment all information will (should) be available. E.g. if you want to get the width of the text
 * in the setter of the "text" attribute, the correct font size might not have been applied yet (depends on the
 * code that is creating and configuring the TextField).
 *
 * For input support, a HTML <input> element is created and bound to this instance. It is NOT being added to this
 * Container but to the <body> of the createjs.Stage's underlying HTML5 web page. To visualize the input as the user
 * would expect it, more items will be added to this Container:
 * - a caret, that indicates the position, where the user can type the next letters
 * - a selection rectangle, that indicates text selection the user has made via mouse or keyboard
 *
 * Also, this implementation has to handle the focus of input fields. You can find the implementation in
 * TextField.onFocus() and TextField.onBlur().
 * The <input> element interacts within that implementation (and receive browser events).
 *
 *
 * When working with the TextField keep in mind that there are plenty of classes that are build "on top" of
 * this implementation dealing with a FRAMEWORK COMPONENT OF the ("blackboxed") FLASH LIBRARY / API. Or better,
 * they are not aware of the fact, that the TextField is not from the flash api but HTML5 canvas / createjs.Text.
 *
 * For more information you should visit the API reference documentation of BOTH flash and createjs:
 *
 * - http://help.adobe.com/de_DE/FlashPlatform/reference/actionscript/3/flash/text/TextField.html
 * - http://createjs.com/docs/easeljs/classes/Text.html
 *
 */
export class TextField extends DisplayObject {
    /**
     * The default TextFormat, that will be used if none specified
     */
    private _defaultTextFormat: TextFormat = new TextFormat();

    public static FontRestriction: FontRestrictionType | null = null;

    /**
     * Used to set this Textfield to the "dirty" state means, it needs to be repainted.
     * The perperty is now public for performance, if this value is true, which mean a redraw is scheduled in next tick, we don't need to explicitly call `cache` or `updateCache`.
     */
    public needsRepaint: boolean = false;

    public htmlLinkFormats: HTMLLinkFormats | null = null;

    // AS3 properties with getters & setters

    /**
     * deprecated attribute from flash
     */
    private _alwaysShowSelection: boolean = true;

    /**
     * Implementation of the autosize behavior of Flash
     */
    private _autoSize: TextFieldAutoSize = TextFieldAutoSize.NONE;

    /**
     * Indicates if this TextField has a background or not (drawing of the bg will be skipped if not)
     */
    private _background: boolean = false;

    /**
     * Set the background color that should be drawn behind the text
     */
    private _backgroundColor: string = "#FFFFFF";

    /**
     * Indicates if this TextField has a border or not
     */
    private _border: boolean = false;

    /**
     * Set the border color
     */
    private _borderColor: string = "#000000";

    /**
     * Toggles if this TextField should not display the text entered (password mode filled up with ***).
     */
    private _displayAsPassword: boolean = false;

    /**
     * The maximum chars the user can input / can be set
     */
    private _maxChars: number = 524288; // this is default value for HTML element

    /**
     * Toggles if this instance has multiple lines
     */
    private _multiline: boolean = false;

    /**
     * A regular expression that will be used when the user inputs text (e.g. email regex pattern)
     */
    private _restrict: RegExp | null = null;

    /**
     * If the text is selectable, this will indicate the char position of the beginning of the selection.
     */
    private _selectionBeginIndex: number | undefined;

    /**
     * If the text is selectable, this will indicate the char position of the end of the selection.
     */
    private _selectionEndIndex: number | undefined;

    private _selectable = false; // TODO: Connect to property `selectable`

    /**
     * Sets the stylesheet to use
     */
    // TODO implement this if needed; So far only getter and setter exists
    private _styleSheet: StyleSheet;

    /**
     * Sets the vertical text alignment: top, bottom, middle
     */
    private _verticalAlign: "top" | "middle" | "bottom" = "top";

    /**
     * Sets the type of this textfield.
     * TextFieldType.DYNAMIC = Display text only
     * TextFieldType.INPUT = Display Text and make it editable
     */
    private _textFieldType: TextFieldType = TextFieldType.DYNAMIC;

    /**
        forces the text to be uppercase at all times
     **/

    private _capitalize:boolean = false;


    /**
     * The textformat that includes information about alignment, linespacing, font, size etc. See
     * TextField.setTextformat() for more information.
     */
    private textFormat: TextFormat = new TextFormat();
    /**
     *  We are scaling the textfields in a higher resolution when caching so that the quality and readability improves
     */
    public defaultCacheScale: number = 1;
    public static scaleCacheForTextfieldsWithFilters: boolean = true;

    private lastMouseDownTime: number;
    private lastMouseDownPos: number | undefined;
    private consecutiveMouseClicks: number;

    public static scaleHTMLByPixelDensity: boolean = false;

    private _INTERNAL_previousLayout: LatestLayoutType | undefined;
    private _INTERNAL_currentLayout: LatestLayoutType | undefined;
    private get latestLayout(): LatestLayoutType {
        /**
         * ⚠️ only used simple properties (_x, _y) in here, as to not cause a recursive loop
         * (as many properties depend on `this.latestLayout`)
         */
        if (this._INTERNAL_currentLayout) {
            return this._INTERNAL_currentLayout;
        } else {
            const initialFormat = { ...this.defaultTextFormat, ...this.textFormat };
            const previousWidth = this._INTERNAL_previousLayout ? this._INTERNAL_previousLayout.size.width : this._width;
            const previousX = this._INTERNAL_previousLayout ? this._INTERNAL_previousLayout.position.x : this._x;

            let layoutParams: LayoutParam;
            if (this._autoSize === TextFieldAutoSize.NONE) {
                layoutParams = {
                    autoSize: TextFieldAutoSize.NONE,
                    x: this._x,
                    y: this._y,
                    width: this._width,
                    height: this._height,
                    lineWidth: this.lineWidth,
                    wordWrap: this._wordWrap,
                    multiline: this._multiline,
                    previousWidth: previousWidth,
                    previousX: previousX,
                    firstVisibleLineIndex: this._firstVisibleLineIndex,
                    verticalAlign: this._verticalAlign,
                };
            } else {
                layoutParams = {
                    autoSize: this._autoSize,
                    x: this._x,
                    y: this._y,
                    width: this._width,
                    height: this._height,
                    wordWrap: this._wordWrap,
                    multiline: this._multiline,
                    previousWidth: previousWidth,
                    previousX: previousX,
                    firstVisibleLineIndex: this._firstVisibleLineIndex,
                    verticalAlign: "top", // always top, as `autoSize` != NONE
                };
            }

            let inputTokens = this._textValue.inputTokens;
            if (this._displayAsPassword) {
                inputTokens = inputTokens.map((token): RichTextInputToken  => {
                    if (token.type === "plain") {
                        const displayText = Array.apply(null, Array(token.text.length))
                        .map(function() {
                            return "•";
                        })
                        .join("");

                        return {
                            type: "plain",
                            text: displayText,
                        };
                    } else {
                        return token;
                    }
                });
            }

            if (!this._multiline) {
                // If the `TextField` is not multiline, remove everything after the first line break, including that itself
                const newLineIndex = inputTokens.findIndex(token => token.type === "newline");
                if (newLineIndex >= 0) {
                    inputTokens = inputTokens.slice(0, newLineIndex);
                }
            }

            const ctx: CanvasRenderingContext2D = (Text as any)._workingContext;
            ctx.save();
            // ctx.setTransform(1, 0, 0, 1, 0, 0);
            this._INTERNAL_currentLayout = calculateLatestLayoutType(ctx, layoutParams, initialFormat, inputTokens, TextField.FontRestriction);
            ctx.restore();
            this._INTERNAL_previousLayout = this._INTERNAL_currentLayout;

            return this._INTERNAL_currentLayout;
        }
    }

    private _firstVisibleLineIndex = 1;

    /**
     * The underlying value backing this `TextField`
     */
    private _textValue: TextValueType = { type: "plain", text: "", inputTokens: [] };

    /**
     * The ID of the interval polling whether this `TextField` is still visible on the `Stage`
     *
     * Input text fields with visible HTML controls are polled to check whether they are still supposed to be visible
     *
     * In case `this` or a parent gets hidden, the `HTML Text Input` gets hidden as well
     *
     * The interval is only active when the text field is visible and focused
     */
    private _textFieldVisibleIntervalId: number | undefined;

    /**
     * The stored width of this component
     */
    private _width: number = 100;

    /**
     * The stored height of this component
     */
    private _height: number = 100;

    /**
     * Hack to be able to interact with the stag in TextField.onRemovedFromStage
     */
    // TODO: remove this variable and its uses as soon as the bug that the stage is already gone in the removed Event
    // has been solved
    private __stageRef: createjs.Stage | undefined; // FOR INTERNAL USE ONLY

    /*
     * INPUT TEXTFIELD
     */

    /**
     * Internally used to indicate if the default text is currently set
     */
    private isDefaultText: boolean = false;

    /**
     * This is the backend for the "input" mode of this Textfield. Since the canvas / createjs has no native
     * input support, a html <input> is needed to capture keyboard events etc.
     */
    private _htmlTextFieldElement: HTMLInputElement | HTMLTextAreaElement | null = null;

    /**
     * Coordinates (global), where the user has pressed the mouse button
     */
    private mouseDownPoint: Point = new Point();

    /**
     * Coordinates (global), where the user has released the mouse button
     */
    private mouseUpPoint: Point = new Point();

    /**
     * Registry that holds all input textfields. Used to implement tab support
     */
    private inputTextFieldService = InputTextFieldService.singleton();

    /**
     * Only applicable to HTML Text
     */
    private _links: Link[] = [];

    /**
     * Only applicable to HTML Text
     */
    private _hoveredLinkIndex: number = -1;

    // private _selection: Selection | undefined;

    /** Workaround for constructor hack */
    private baseConstructorRan: boolean = false;

    constructor() {
        super();

        this.baseConstructorRan = true;

        // setting default performance-related properties
        this.mouseEnabled = this.type === TextFieldType.INPUT || this._selectable; // TODO: This needs to be updated when setting

        this.addEventListener(Event.ADDED_TO_STAGE, this.onAddedToStage);
        this.addEventListener(Event.REMOVED_FROM_STAGE, this.onRemovedFromStage);

        this.ensureProperHTMLTextFieldElement();
    }

    /**
     * Indicates if this TextField has the (keyboard) focus right now
     */
    public get isFocused(): boolean {
        return !!(this._htmlTextFieldElement && document.activeElement === this._htmlTextFieldElement);
    }

    /**
     * Will be called whenever this instance will be added to the stage (or usually a child of the stage)
     * @param {createjs.Event} e
     */
    private onAddedToStage = (e: Event): void => {
        this.__stageRef = this.stage;

        if (this.isInputTextField()) {
            this.ensureProperHTMLTextFieldElement();
        }

        if (this.isInputTextField() || this._selectable) {
            this.stage.addEventListener(STAGE_MOUSE_DOWN, this.onStageMouseDown);
            this.setupListenersForInputSupport();
        }
        if (this.text) {
            this.invalidate("Added to stage with text already assigned");
        }
    }

    public getBounds(targetSpace?: createjs.DisplayObject): createjs.Rectangle {
        const bounds = this.latestLayout.bounds.clone();

        if (targetSpace && targetSpace !== this) {
            return this.transformRect(bounds, targetSpace);
        } else {
            return bounds;
        }
    }

    /**
     * Ensures that the interval check for the `TextField` visibility is active, if an HTML Text Input is used and focused
     *
     * If that is not the case, the interval is cleaned up
     */
    private updateTextFieldVisibleInterval() {
        if (this.isInputTextField() && this._htmlTextFieldElement && document.activeElement === this._htmlTextFieldElement) {
            if (!this._textFieldVisibleIntervalId) {
                this._textFieldVisibleIntervalId = window.setInterval(() => {
                    this.blurHTMLTextInputIfTextFieldIsNotVisible();
                }, 100);
            }
        } else {
            if (this._textFieldVisibleIntervalId) {
                window.clearInterval(this._textFieldVisibleIntervalId);
                this._textFieldVisibleIntervalId = undefined;
            }
        }
    }

    /**
     * Calls `blur()` on the HTML "Text Field" if the stage element or one of its parents is not visible
     *
     * Also stops the polling interval
     */
    private blurHTMLTextInputIfTextFieldIsNotVisible() {
        let isVisible = this.isVisible();
        let current: createjs.DisplayObject | null = this;
        while (current && isVisible) {
            current = current.parent;
            if (current) {
                isVisible = current.isVisible();
            }
        }

        if (isVisible) {
            /**
             * Check whether the is still visually on top of the stage
             * Mouse disabled objects are excluded from the check.
             */

            const bounds = this.getBounds();
            const pos = this.localToGlobal(bounds.x, bounds.y);
            isVisible = this.stage.getObjectUnderPoint(pos.x + 1, pos.y + 1, 1) === this;
        }

        if (!isVisible && this._htmlTextFieldElement) {
            logger.info(`hiding the TextField ${this.name}, as it or its parents are not visibile.`);
            if (document.activeElement === this._htmlTextFieldElement) {
                this._htmlTextFieldElement.blur(); // this will in turn also stop the interval
            }
        }
    }


    /**
     * Will be called whenever this instance is being removed from the stage (or usually a child of the stage)
     * @param {createjs.Event} e
     */
    private onRemovedFromStage = (e: Event): void => {
        // createjs.Ticker.removeEventListener("tick", this.validate);
        if (this.cacheID !== 0) {
            this.uncache();
        }

        // Teardown input handlers
        if (this.isInputTextField()) {
            this.teardown();
        } else if (this.selectable) {
            this.removeMouseInputHandlers();
        }

        if (this._htmlTextFieldElement) {
            logger.error(`removed from stage, but still having a reference to an _htmlInputElement`, this.name);
        }

        if (this.__stageRef) {
            this.__stageRef.removeEventListener(STAGE_MOUSE_DOWN, this.onStageMouseDown);
            this.__stageRef.removeEventListener(createjs.MouseEvent.MOUSE_MOVE, this.handleStageMouseMove);
        }

        this.__stageRef = undefined;
    }

    private findLink(mousePoint: Point): number {
        return this._links.findIndex((link) => link.containsPoint(mousePoint));
    }

    private handleStageMouseMove = (e: createjs.MouseEvent) => {
        const mousePoint = this.normalizeCoordinates(e);

        const linkIndex = mousePoint ? this.findLink(mousePoint) : -1;

        if (linkIndex !== this._hoveredLinkIndex) {
            this._hoveredLinkIndex = linkIndex;

            if (linkIndex > -1) {
                Mouse.instance().showCustomCursor(MouseCursor.CLICK);
            } else if (mousePoint && this.getBounds().containsPoint(mousePoint)) {
                Mouse.instance().showCustomCursor(MouseCursor.ARROW);
            }

            this.invalidate(`link changed`);
        }
    }

    private handleMouseClick = (e: createjs.MouseEvent) => {
        const mouseDownPoint = this.normalizeCoordinates(e);
        if (!mouseDownPoint) {
            return;
        }
        const linkIndex = this.findLink(mouseDownPoint);
        const link = this._links[linkIndex];

        if (link) {
            logger.debug("link clicked", link.linkId);
            if (link.isAnchorLink) {
                navigateToURL(link.linkId);
            } else {
                this.dispatchEvent(new TextEvent(TextEvent.LINK, true, false, link.linkId));
            }
        }
    }

    /**
     * Eventhandler that captures every mouse down event anywhere on the stage.
     * Used to blur (unfocus) an input text field
     * @param {createjs.Event} e
     */
    private onStageMouseDown = (e: Event): void => {
        const mouseDownPoint = this.normalizeCoordinates(e);
        if (this.isFocused && mouseDownPoint && !this.isPointWithinHitArea(mouseDownPoint) && this._htmlTextFieldElement) {
            this._htmlTextFieldElement.blur();
        }

        if (this._selectionBeginIndex !== undefined) {
            selectionText = undefined;
            this._selectionBeginIndex = undefined;
            this._selectionEndIndex = undefined;

            this.invalidate(`stage mouse down, no more selection`, true);
        }
    }

    /*
     * AS3 mock-up - unused functions
     */
    /** @deprecated **/ public antiAliasType: string = AntiAliasType.NORMAL;
    /** @deprecated **/ public gridFitType: string = GridFitType.NONE;
    /** @deprecated **/ public caretIndex: number = 0;
    /** @deprecated **/ public condenseWhite: boolean = false;
    /** @deprecated **/ public embedFonts: boolean = false;
    /** @deprecated **/ public sharpness: number = 0;
    /** @deprecated **/ public textInteractionMode: string = "normal";
    /** @deprecated **/ public thickness: number = 1;
    /** @deprecated ? **/ public mouseWheelEnabled: boolean = false; // TODO: @HTML5 multiline text implementation as DOM element

    // TODO: @HTML5 InteractiveObject properties, should be placed in augmentation of display object
    /** @deprecated **/ public tabIndex: number = 0;
    /** @deprecated **/ public tabEnabled: boolean = false;

    /**
     * @deprecated
     */
    public replaceText(beginIndex: number, endIndex: number, newText: string): void {
        logger.warn(`replaceText is not implemented`);
    }

    /**
     * @deprecated
     */
    public get alwaysShowSelection(): boolean {
        return this._alwaysShowSelection;
    }

    /**
     * @deprecated
     */
    public set alwaysShowSelection(value: boolean) {
        this._alwaysShowSelection = value;
    }

    public get selectable(): boolean {
        return this._selectable;
    }

    public set selectable(value: boolean) {
        const changed = this._selectable !== value;

        if (changed) {
            this._selectable = value;
            this.mouseEnabled = value;
            if (this._selectable && !this._mouseHandlersAttached) {
                this.setupListenersForInputSupport();
                if ( this.stage ) {
                    this.stage.addEventListener(STAGE_MOUSE_DOWN, this.onStageMouseDown);
                }
            } else if (!this._selectable && this._mouseHandlersAttached) {
                this.removeMouseInputHandlers();
                if (this.__stageRef) {
                    this.__stageRef.removeEventListener(STAGE_MOUSE_DOWN, this.onStageMouseDown);
                }
            }

            this.invalidate(`selectable property changed`);
        }
    }

    /**
     * Sets as selected the text designated by the index values of the first and last characters,
     * which are specified with the beginIndex and endIndex parameters.
     * @param beginIndex
     * @param endIndex
     */
    public setSelection(beginIndex: number, endIndex: number): void {
        if (this._htmlTextFieldElement) {
            this._htmlTextFieldElement.selectionStart = beginIndex;
            this._htmlTextFieldElement.selectionEnd = endIndex;
        } else {
            this._selectionBeginIndex = beginIndex;
            this._selectionEndIndex = endIndex;

            this.updateSelectedText();

            this.invalidate(`selection changed`);
        }
    }

    /**
     * @returns {TextFieldAutoSize}
     */
    public get autoSize(): TextFieldAutoSize {
        return this._autoSize;
    }

    /**
     * @param TextFieldAutoSize
     */
    public set autoSize(value: TextFieldAutoSize) {
        const changed = this._autoSize !== value;

        if (changed) {
            this._autoSize = value;

            this.invalidate(`autosize property changed`);
        }
    }

    // /**
    //  * Adjusts the width and height of the text field to the current text. Also adjusts the x position to keep as3 auto sizing behavior
    //  *
    //  * @param previousWidth
    //  */
    // private autoSizeTextField(metrics: LayedOutTextMetrics | undefined = this.latestLayout.metrics): void {

    // }

    /**
     * Returns true if background is visible, false by default
     * @returns {boolean}
     */
    get background(): boolean {
        return this._background;
    }

    /**
     * Create/update background shape
     * @param value
     */
    set background(value: boolean) {
        const changed = value !== this.background;

        this._background = value;

        if (changed) {
            this.invalidate(`background property changed`);
        }
    }

    public glow: GlowType[] = [];
    public dropShadow: ShadowType[] = [];

    /**
     * The color of the text field background. The default value is #FFFFFF
     * @returns {string}
     */
    get backgroundColor(): string {
        return this._backgroundColor;
    }

    /**
     * The color of the text field background
     * @param color
     */
    set backgroundColor(value: string) {
        const changed = this._backgroundColor !== value;

        this._backgroundColor = value;

        if (changed) {
            this.invalidate(`backgroundColor property changed`);
        }
    }

    /**
     * Returns true if border is visible, false by default
     * @returns {boolean}
     */
    get border(): boolean {
        return this._border;
    }

    /**
     * Create/update border shape
     * @param value
     */
    set border(value: boolean) {
        const changed = this._border !== value;

        this._border = value;

        if (changed) {
            this.invalidate(`border property changed`);
        }
    }

    /**
     * The color of the text field border. The default value is #000000
     * @returns {string}
     */
    get borderColor(): string {
        return this._borderColor;
    }

    /**
     * The color of the text field border
     * @param value
     */
    set borderColor(value: string) {
        const changed = this._borderColor !== value;

        this._borderColor = value;

        if (changed) {
            this.invalidate(`borderColor property changed`);
        }
    }

    /**
     * If TextFormat is not specified for textfield, defaultTextFormat will be applied
     * @returns {TextFormat}
     */
    get defaultTextFormat(): TextFormat {
        return this._defaultTextFormat;
    }

    /**
     * If TextFormat is not specified for textfield, defaultTextFormat will be applied
     * @param value
     */
    set defaultTextFormat(value: TextFormat) {
        const changed = !isEqual(this._defaultTextFormat, value);

        this._defaultTextFormat = { ...value };

        if (changed) {
            this.invalidate(`defaultTextFormat property changed`);
        }
    }

    /**
     * Specifies whether the text field is a password text field
     * @returns {boolean}
     */
    get displayAsPassword(): boolean {
        return this._displayAsPassword;
    }

    /**
     * Specifies whether the text field is a password text field
     * @param value
     */
    set displayAsPassword(value: boolean) {
        const changed = this._displayAsPassword !== value;
        this._displayAsPassword = value;
        if (this._htmlTextFieldElement && this._htmlTextFieldElement instanceof HTMLInputElement) {
            this._htmlTextFieldElement.type = this._displayAsPassword ? "password" : "text";
            if (changed) {
                this.invalidate(`displayAsPassword property changed`);
            }
        } else if (value === true) {
            logger.error(`Can't set displayAsPassword, as the underlying element is not a HTMLInputElement instance`, this._htmlTextFieldElement);
        }
    }

    /**
     * The maximum number of characters that the text field can contain, as entered by a user. If 0 - unlimited
     * @returns {number}
     */
    get maxChars(): number {
        return this._maxChars;
    }

    /**
     * The maximum number of characters that the text field can contain, as entered by a user. If 0 - unlimited
     * @param value
     */
    set maxChars(value: number) {
        const changed = this._maxChars !== value;

        this._maxChars = value;

        if (this._htmlTextFieldElement) {
            this._htmlTextFieldElement.maxLength = this._maxChars;
        }

        if (changed) {
            this.invalidate(`maxChars property changed`);
        }
    }

    /**
     * @HTML5 Standard text, no special format implemented
     * @returns {string}
     */
    get htmlText(): string {
        if (this._textValue.type !== "html") {
            logger.error(`TextField.htmlText: Current text is not HTML text`);
        }

        return this._textValue.text;
    }

    /**
     * @HTML5 Standard text, no special format implemented
     * @param value
     */
    set htmlText(value: string) {
        value = unescape(value);

        const changed = this._textValue.type !== "html" || this._textValue.type !== value;

        if (changed) {
            this._textValue = { type: "html", text: value, inputTokens: parseHTMLText(value) };
            this._hoveredLinkIndex = -1;

            this.invalidate(`htmlText property changed`);
        }
    }

    private drawHTMLText(ctx: CanvasRenderingContext2D | null, metrics: { width?: number; height?: number } | null, lines: string[] | undefined): { width?: number; height?: number } | null {
        const paint = !!ctx;
        if (!paint) {
            ctx = (Text as any)._workingContext;
            if (!ctx) {
                throw new Error(`Did not get ctx`);
            }
        }

        if (!ctx) {
            throw new Error(`Did not get ctx`);
        }

        // for textfield with filters, we need to make a slightly larger clip, for now we only make a work around instead of calculation
        let clipCompensateForFilters = 0;
        if ( this.filters && this.filters.length > 0) {
            clipCompensateForFilters = 2;
        }
        ctx.save();
        ctx.beginPath();
        ctx.rect(0 - clipCompensateForFilters, 0, this.width + (clipCompensateForFilters * 2), this.height);
        ctx.clip();

        ctx.textBaseline = "alphabetic";

        const selection = this._selectable && this._selectionBeginIndex !== undefined && this._selectionEndIndex !== undefined ? { start: this._selectionBeginIndex, end: this._selectionEndIndex } : undefined;

        const initialState: TextDrawingState = {
            xOffset: 0,
            yOffset: 0,
            distanceToBaseline: this.latestLayout.metrics.distanceToBaseline,
            currentLink: null,
            lineHeight: this.latestLayout.metrics.lineHeight, // should only affect 2nd line and onwards, first line must be unaffected
            selection: selection,
            printedCharacters: 0,
            underLinePlacement: this.latestLayout.metrics.distanceToBaseline * 0.125, // distance from top
            currentFormat: { ...this.defaultTextFormat, ...this.textFormat },
            maxWidth: this.lineWidth && Math.min(this.lineWidth, this.width) || this.width, // Maximum width, at which to ellipse or break the text
            shouldPaint: paint,
            closedLinks: [],
            glow: this.glow,
            dropShadow: this.dropShadow,
            htmlLinkFormats: this.htmlLinkFormats
        };

        const layoutMetrics = this.latestLayout.metrics;

        if (DEBUG_TEXT_DRAWINGS) {
            ctx.fillStyle = `rgba(255, 0, 0, 0.5)`;
            ctx.fillRect(0, 0, this.width, this.height);

            ctx.fillStyle = `rgba(0, 255, 0, 0.5)`;
            ctx.fillRect(0, layoutMetrics.textHeight * 0.4, layoutMetrics.widthUntilEndOfText, layoutMetrics.textHeight * 0.2);

            // draw blue areas in top and bottom fifth of textHeight/textWidth rect
            ctx.fillStyle = `rgba(0, 0, 255, 0.5)`;
            const gutterHeight = (layoutMetrics.textWithGutterHeight - layoutMetrics.textHeight) / 2;
            ctx.fillRect(layoutMetrics.widthUntilEndOfText - layoutMetrics.textWidth, gutterHeight, layoutMetrics.textWidth, layoutMetrics.textHeight / 5);
            ctx.fillRect(layoutMetrics.widthUntilEndOfText - layoutMetrics.textWidth, layoutMetrics.textHeight * 0.8 + gutterHeight, layoutMetrics.textWidth, layoutMetrics.textHeight / 5);
        }

        applyFormat(ctx, initialState.currentFormat, TextField.FontRestriction);

        const finalState = this.latestLayout.drawingTokens.reduce((state, token) => {
            switch (token.type) {
                case "set-x-y":
                    return setXMoveY(state, token.x, token.y); // Needed for link handling
                case "advance-x":
                    return {
                        ...state,
                        xOffset: state.xOffset + token.amount,
                    };
                case "start-link":
                    state = startLink(state, token.linkId, this._hoveredLinkIndex, token.isAnchorLink);
                    applyFormat(ctx!, state.currentFormat, TextField.FontRestriction);
                    return state;
                case "plain": {
                    const chunkWidth = token.width;
                    return drawText(ctx!, state, token.text, chunkWidth, this.latestLayout.size.height); // `ctx` is set, but TS doesn't know it
                }
                case "end-link":
                    state = finalizeLink(state);
                    applyFormat(ctx!, state.currentFormat, TextField.FontRestriction);
                    return state;
                case "switch-to-format":
                    state = applyFormatToState(state, token.format);
                    applyFormat(ctx!, state.currentFormat, TextField.FontRestriction);
                    return state;
                default:
                    return state;
            }
        }, initialState);

        ctx.restore();

        if (metrics) {
            metrics.width = layoutMetrics.textWidth;
            metrics.height = layoutMetrics.textHeight;
        }

        this._links = finalState.closedLinks;

        if (this.stage) {
            this.stage.removeEventListener(createjs.MouseEvent.MOUSE_MOVE, this.handleStageMouseMove);
            if (this._links.length > 0) {
                this.mouseEnabled = true;
                this.stage.addEventListener(createjs.MouseEvent.MOUSE_MOVE, this.handleStageMouseMove);
                this.addEventListener(createjs.MouseEvent.CLICK, this.handleMouseClick);
                if (DEBUG_LINK_RANGE) {
                    ctx.save();
                    ctx.strokeStyle = "#FFFFFF";
                    this._links.forEach( l => {
                        l.rects.forEach( r => {
                            ctx!.strokeRect(r.x, r.y, r.width, r.height);
                        });
                    });
                    ctx.restore();
                }
            }
        }

        return metrics;
    }

    /**
     * The number of characters in a text field.
     * @returns {number}
     */
    get length(): number {
        return this.text.length;
    }

    /**
     * Indicates whether field is a multiline text field
     * @returns {boolean}
     */
    get multiline(): boolean {
        return this._multiline;
    }

    /**
     * Indicates whether field is a multiline text field
     * @param value
     */
    set multiline(value: boolean) {
        const changed = value !== this._multiline;

        this._multiline = value;

        if (changed) {
            this.ensureProperHTMLTextFieldElement();
            this.invalidate(`multiline property changed`);
        }
    }

    // Ensures that the proper text control (`input` or `textarea`) is available for the control
    private ensureProperHTMLTextFieldElement() {
        if (this.type === TextFieldType.INPUT) {
            let inputElement: HTMLInputElement | HTMLTextAreaElement;
            if (this.multiline) {
                if (this._htmlTextFieldElement && this._htmlTextFieldElement instanceof HTMLTextAreaElement) {
                    if(this.stage) {
                        this.inputTextFieldService.registerInputField(this);
                    }
                    return; // Already correct type
                } else if (this._htmlTextFieldElement) {
                    this.inputTextFieldService.unregisterInputField(this);
                    document.body.removeChild(this._htmlTextFieldElement);
                    this._htmlTextFieldElement = null;
                }
                inputElement = document.createElement("textarea");
            } else {
                if (this._htmlTextFieldElement && this._htmlTextFieldElement instanceof HTMLInputElement) {
                    if(this.stage) {
                        this.inputTextFieldService.registerInputField(this);
                    }
                    return; // Already correct type
                } else if (this._htmlTextFieldElement) {
                    this.inputTextFieldService.unregisterInputField(this);
                    document.body.removeChild(this._htmlTextFieldElement);
                    this._htmlTextFieldElement = null;
                }
                inputElement = document.createElement("input");
                inputElement.type = this._displayAsPassword ? "password" : "text";
                inputElement.autocomplete = "off";
                inputElement.spellcheck = false; // only really not desired for username field
            }

            this._htmlTextFieldElement = inputElement;
            inputElement.className = HTML_TEXTFIELD_CLASS;
            inputElement.id = `${this.name}_${++textFieldCount}`;
            inputElement.style.textAlign = this.textFormat ? this.textFormat.align : "left";
            inputElement.maxLength = this._maxChars;
            inputElement.value = this._textValue.text;
            document.body.appendChild(inputElement);

            inputElement.addEventListener("input", this.onInput);
            inputElement.addEventListener("keydown", this.onKeyDown);
            inputElement.addEventListener("keyup", this.onKeyUp);
            inputElement.addEventListener("blur", (e) => {
                this.onBlur();
                this.dispatchEvent(FocusEvent.FOCUS_OUT);
                this.invalidate(`inputElement blur event`);
            });
            if(this.stage) {
                this.inputTextFieldService.registerInputField(this);
            }

            if (this.isFocused) {
                // input should be focused, but has just been changed to another element, so re-focussing
                this.onFocus();
            }
        } else {
            // no element needed, remove from DOM
            if (this._htmlTextFieldElement && this._htmlTextFieldElement.parentElement === document.body) {
                document.body.removeChild(this._htmlTextFieldElement);
                this.inputTextFieldService.unregisterInputField(this);
                this._htmlTextFieldElement = null;
            } else if (this._htmlTextFieldElement) {
                logger.error(`ensureProperTextElement: has this._htmlInputElement but on unexpected node (not body)`);
            }

            this._htmlTextFieldElement = null;
        }
    }

    /**
     * Defines the number of text lines in a multiline text field
     */
    public get numLines() {
        return this.latestLayout.metrics.totalNumberOfTextLines;
    }

    /**
     * Indicates the set of characters that a user can enter into the text field.
     * @returns {string}
     */
    public get restrict(): string | null {
        return this._restrict && this._restrict.source;
    }

    /**
     * Indicates the set of characters that a user can enter into the text field.
     * Uses 'g' as default flag. To customize the flag set regEx directly using restrictRegEx function.
     * @param value
     */
    public set restrict(value: string | null) {
        if (value == null || value.length === 0) {
            this._restrict = null;
        } else {
            // Replacement used because of native string formatting and second RegExp formatting.
            value = value.replace("\\", "\\\\"); // result is special character restriction
            value = value.replace("\\\\", "\\\\\\"); // result is '\' restriction
            value = "[^(" + value + ")]";
            this._restrict = new RegExp(value, "g");
        }
    }

    /**
     * Additional function if restrict function cause problems due to as3-ts incompatibility
     * @param value
     */
    public set restrictRegEx(value: RegExp) {
        this._restrict = value;
    }

    /**
     *  The vertical position of text in a text field. The scrollV property is useful for directing users to a specific paragraph in a long passage, or creating scrolling text fields.
     *
     * The units of vertical scrolling are lines, whereas the units of horizontal scrolling are pixels. If the first line displayed is the first line in the text field, scrollV is set to 1 (not 0).
     *
     * https://help.adobe.com/en_US/FlashPlatform/reference/actionscript/3/flash/text/TextField.html#scrollV
     */
    public get scrollV(): number {
        return this._firstVisibleLineIndex;
    }

    // Set the first visible line, starting at index `1`
    public set scrollV(value: number) {
        value = Math.min(this.maxScrollV, Math.max(value, 1));
        const changed = this._firstVisibleLineIndex !== value;
        this._firstVisibleLineIndex = value;
        if (this._htmlTextFieldElement) {
            this._htmlTextFieldElement.scrollTop = this.latestLayout.metrics.lineHeight * (value - 1);
        }
        if (changed) {
            this.invalidate(`scrollV property changed`);
        }
    }

    public get maxScrollV(): number {
        // Example: We have 6 lines total but only 2 visible. Hence the `maxScrollV` should be 5 such that when set the last 2 lines are visible
        return Math.max(this.latestLayout.metrics.totalNumberOfTextLines - this.latestLayout.metrics.maximumNumberOfVisibleLines + 1, 1);
    }

    /**
     * The zero-based character index value of the first character in the current selection.
     * @returns {number}
     */
    public get selectionBeginIndex(): number | undefined {
        return this._selectionBeginIndex;
    }

    /**
     * The zero-based character index value of the last character in the current selection.
     * @returns {number}
     */
    public get selectionEndIndex(): number | undefined {
        return this._selectionEndIndex;
    }

    /**
     * @deprecated
     * Attaches a style sheet to the text field.
     * Text does not support HTML text, and can only display one font style at a time
     * @returns {StyleSheet}
     */
    public get styleSheet(): StyleSheet {
        return this._styleSheet;
    }

    /**
     * @deprecated
     * Attaches a style sheet to the text field.
     * Text does not support HTML text, and can only display one font style at a time
     * @param value
     */
    public set styleSheet(value: StyleSheet) {
        this._styleSheet = value;
    }

    /**
     * A string that is the current text in the text field.
     * @returns {string}
     */
    public get text(): string {
        if (this._displayAsPassword === true && this._htmlTextFieldElement != null) {
            return this._htmlTextFieldElement.value;
        }

        return this._textValue.text;
    }

    /**
     * A string that is the current text in the text field.
     * @param value
     */
    public set text(value: string) {
        value = unescape(value);

        if( this._capitalize ) {
            value = value.toLocaleUpperCase();
        }

        const changed = this._textValue.type !== "plain" || this._textValue.text !== value;

        if (this._htmlTextFieldElement) {
            this._htmlTextFieldElement.value = value;
        }

        if (changed) {
            this._textValue = { type: "plain", text: value, inputTokens: parsePlainText(value) };
            if (value === "") {
                this.uncache();
            } else {
                this.invalidate(`text property updated`);
            }
        }
    }

    /**
     * The color of the text in a text field, in hexadecimal format.
     * @returns {string}
     */
    public get textColor(): string | number {
        return this.getTextFormat().color as string;
    }

    /**
     * The color of the text in a text field, in hexadecimal format.
     * @param value
     */
    public set textColor(value: string | number) {
        value = ColorTransform.colorNumToString(value);

        let changed: boolean;
        if (this.textFormat) {
            changed = this.textFormat.color !== value;
            this.textFormat.color = value;
        } else {
            changed = this.defaultTextFormat.color !== value;
            this.defaultTextFormat.color = value;
        }

        if (changed) {
            this.invalidate(`textColor property changed`);
        }
    }

    public get align(): string {
        return this.getTextFormat().align;
    }

    /**
     * The color of the text in a text field, in hexadecimal format.
     * @param value
     */
    public set align(value: string) {
        let changed: boolean;
        if (this.textFormat) {
            changed = this.textFormat.align !== value;
            this.textFormat.align = value;
        } else {
            changed = this.defaultTextFormat.align !== value;
            this.defaultTextFormat.align = value;
        }

        if (changed) {
            if (this._htmlTextFieldElement) {
                this._htmlTextFieldElement.style.textAlign = value || "left";
            }
            this.invalidate(`align property changed`);
        }
    }

    /**
     * The height of the text in pixels
     * @returns {number}
     */
    get textHeight(): number {
        return this.latestLayout.metrics.textHeight;
    }

    /**
     * The width of the text in pixels
     * @returns {number}
     */
    get textWidth(): number {
        return this.latestLayout.metrics.textWidth;
    }

    /**
     * The width of a single line of text in pixels
     * @param value If null, the text will not be wrapped.
     */
    private setLineWidth(value: number | null) {
        const changed = value !== this.lineWidth;

        if (changed) {
            this.lineWidth = value;

            this.invalidate(`lineWidth property changed`);
        }
    }

    private lineWidth: number | null = null;

    get verticalAlign(): string {
        return this._verticalAlign;
    }

    set verticalAlign(value: string) {
        if (value !== "top" && value !== "middle" && value !== "bottom") {
            logger.error(`TextField.verticalAlign: Unsupported value "${value}"`);
            return;
        }

        const changed = this._verticalAlign !== value;
        this._verticalAlign = value;

        if (changed) {
            this.invalidate(`verticalAlign property changed`);
        }
    }

    private _wordWrap: boolean = false;

    /**
     * If the value of wordWrap is false, text outside the textfield borders will be cut
     * @returns {boolean}
     */
    get wordWrap(): boolean {
        return this._wordWrap;
    }

    /**
     * If the value of wordWrap is false, text outside the textfield borders will be cut
     * @param value
     */
    set wordWrap(value: boolean) {
        const changed = this._wordWrap !== value;

        // TODO: Align with https://help.adobe.com/en_US/FlashPlatform/reference/actionscript/3/flash/text/TextField.html#wordWrap
        // this.setLineWidth(value ? this._width : null);

        if (changed) {
            this._wordWrap = value;

            this.invalidate(`wordWrap property changed`);
        }
    }

    /**
     * Dynamic or input
     * @returns {TextFieldType}
     */
    get type(): TextFieldType  {
        return this._textFieldType;
    }

    set type(value: TextFieldType) {
        const changed = value !== this._textFieldType;

        if (changed) {
            this._textFieldType = value;

            this.ensureProperHTMLTextFieldElement();

            if (this.isInputTextField()) {
                this._autoSize = TextFieldAutoSize.NONE;
                this.mouseEnabled = this.type === TextFieldType.INPUT || this._selectable;
                this.setupListenersForInputSupport();
                if (this.stage) {
                    this.stage.addEventListener(STAGE_MOUSE_DOWN, this.onStageMouseDown);
                }
            }

            this.invalidate(`type property changed`);
        }
    }

    // Allow type enum to be set using a string (wrapper property that uses type)
    // Needed for Asset build compatibility
    get textType(): string  {
        return this._textFieldType === TextFieldType.DYNAMIC ? "dynamic" : "input";
    }

    set textType(value: string) {
         if (value === "dynamic") {
            this.type = TextFieldType.DYNAMIC;
         } else {
            this.type = TextFieldType.INPUT;
         }
    }

    // override
    get width(): number {
        return this.latestLayout.size.width;
    }

    // override
    set width(value: number) {
        const changed = this._width !== value;

        // efficient layout update
        if (changed && this.baseConstructorRan) {
            this._width = value;

            this.invalidate(`width property changed`);
        }
    }

    private _x: number = 0;
    private _y: number = 0;

    get x(): number {
        return this.latestLayout.position.x;
    }

    set x(value: number) {
        const changed = this._x !== value;

        // efficient layout update
        if (changed && this.baseConstructorRan) {
            this._x = value;

            this.invalidate(`x property changed`);
        }
    }

    get y(): number {
        return this.latestLayout.position.y;
    }

    set y(value: number) {
        const changed = this._y !== value;

        // efficient layout update
        if (changed && this.baseConstructorRan) {
            this._y = value;

            this.invalidate(`y property changed`);
        }
    }

    // override
    set height(value: number) {
        const changed = this._height !== value;

        // efficient layout update
        if (changed && this.baseConstructorRan) {
            this._height = value;

            this.invalidate(`height property changed`);
        }
    }

    // override
    /**
     * Returns the `TextField`'s set height, or if none is set the full height of the contained text
     */
    get height(): number {
        return this.latestLayout.size.height;
    }

    /**
     * Forces width and height of the text field to the provided values
     * @param width
     * @param height
     */
    // private forceBounds(width: number, height: number, dontInvalidate: boolean = false): void {
    //     const changed = width !== this._width || height !== this._height;

    //     this._width = width;
    //     this._height = height;
    //     this.setBounds(0, 0, width, height);

    //     if (changed && !dontInvalidate) {
    //         this.invalidate(`forceBounds called`);
    //     }
    // }

    /**
     * Public methods
     */

    public cache(x: number, y: number, width: number, height: number, scale?: number | undefined) {
        scale = this.prepareFiltersForScaledCache(scale);
        super.cache(x, y, width, height, scale);
        if (TextField.CACHE_UPDATED_EVENTS_ENABLED) {
            this.dispatchEvent(new createjs.Event(TextField.CACHE_UPDATED_EVENT_TYPE, true, false));
        }
    }

    private prepareFiltersForScaledCache(scale: number | undefined) {
        // no need to scale up filters if the textfield itself isn't scaled
        if (scale !== undefined && scale > 1) {
            let scaleFactorX: number = 0;
            let scaleFactorY: number = 0;

            // if caching with a bigger scale: increase filters as well
            if (scale !== undefined && scale > 1 && this.filters && this.filters.length > 0) {
                if (!TextField.scaleCacheForTextfieldsWithFilters) {
                    scale = 1;
                } else {
                    for (let i: number = 0; i < this.filters.length; i++) {
                        let filter: Filter = this.filters[i];
                        if ((filter instanceof GlowFilter || filter instanceof DropShadowFilter) && !filter.isScaled) {
                            if (filter instanceof DropShadowFilter) {
                                (filter as DropShadowFilter).distance *= scale;
                            } else {
                                (filter as GlowFilter).blurX *= scale;
                                (filter as GlowFilter).blurY *= scale;
                            }

                            filter.isScaled = true;
                            scaleFactorX = Math.max(filter.blurX / scale, scaleFactorX);
                            scaleFactorY = Math.max(filter.blurY / scale, scaleFactorY);
                        }
                    }
                }
            }

            // if the position has changed because of the glow, slightly move the textfield to correct it's position
            if (scaleFactorX > 0) {
                this.x += scaleFactorX;
            }
            if (scaleFactorY > 0) {
                this.y += scaleFactorY;
            }
        }
        return scale;
    }

    public static readonly CACHE_UPDATED_EVENT_TYPE = "TEXTFIELD_CACHE_UPDATED";

    public static CACHE_UPDATED_EVENTS_ENABLED = false;

    /**
     * Appends the string specified by the newText parameter to the end of the text of the text field
     * @param newText
     */
    public appendText(newText: string) {
        const text = this._textValue.text + newText;

        if (this._textValue.type === "plain") {
            this._textValue = {
                type: this._textValue.type,
                text: text,
                inputTokens: parsePlainText(text),
            };
        } else {
            // HTML
            this._textValue = {
                type: this._textValue.type,
                text: text,
                inputTokens: parseHTMLText(text),
            };
        }

        this.invalidate(`text updated`);
    }

    /**
     * Get TextLineMetrics for specified line in multiline textfield
     * @param lineIndex
     * @returns {TextLineMetrics}
     */
    public getLineMetrics(lineIndex: number): TextLineMetrics {
        const lineText = this.latestLayout.metrics.drawnLines[lineIndex];

        const ctx = (Text as any)._workingContext;
        applyFormat(ctx, this.textFormat, TextField.FontRestriction);

        return new TextLineMetrics(this.x, ctx.measureText(lineText).width, this.latestLayout.metrics.lineHeight);
    }

    public getMetrics() {
        return {
            lines: this.latestLayout.metrics.drawnLines,
            lineHeight: this.latestLayout.metrics.lineHeight,
            vOffset: 0,
            width: this.latestLayout.metrics.textWidth,
            height: this.latestLayout.metrics.textHeight,
        };
    }

    /**
     * Get text as string from specified text line in multiline textfield
     * @param lineIndex
     * @returns string
     */
    public getLineText(lineIndex: number): string {
        return this.latestLayout.metrics.drawnLines[lineIndex];
    }

    public setTransform(x?: number) {
        return this;
    }

    /**
     * Text does not support HTML text, and can only display one font style at a time
     * @param beginIndex - forced as3 compatibility
     * @param endIndex - forced as3 compatibility
     * @returns {TextFormat}
     */
    public getTextFormat(beginIndex: number = -1, endIndex: number = -1): TextFormat {
        if (this.textFormat) {
            return this.textFormat;
        } else {
            return this.defaultTextFormat;
        }
    }

    /**
     * Text does not support HTML text, and can only display one font style at a time
     * @param format
     * @param beginIndex - forced as3 compatibility
     * @param endIndex - forcedas3 compatibility
     */
    public setTextFormat(format: TextFormat, beginIndex: number = -1, endIndex: number = -1): void {
        if (currentBrowserInfo.isIE && /^_/.test(format.font)) {
            format.font = format.font.substring(1); // remove _ for IE
        }
        const changed = !isEqual(this.textFormat, format);

        this.textFormat = { ...format }; // need to copy here, as the caller might keep a reference
        if (changed) {
            if (this._htmlTextFieldElement) {
                this._htmlTextFieldElement.style.textAlign = this.textFormat.align || "left";
            }
            this.invalidate(`textFormat property changed`);
        }
    }

    /**
     * Replaces the current selection with the contents of the value parameter.
     * The text is not treated as HTML
     * @param value
     */
    public replaceSelectedText(value: string): void {
        if (this._htmlTextFieldElement && this._htmlTextFieldElement.selectionStart !== this._htmlTextFieldElement.selectionEnd) {
            const htmlText = this._htmlTextFieldElement.value;
            const start = this._htmlTextFieldElement.selectionStart === null ? undefined : this._htmlTextFieldElement.selectionStart;
            const end = this._htmlTextFieldElement.selectionEnd === null ? undefined : this._htmlTextFieldElement.selectionEnd;
            const newText = htmlText.slice(0, start) + value + htmlText.slice(end, htmlText.length);
            this._htmlTextFieldElement.value = newText;
            this.invalidate(`replaceSelectedText`);
        }
    }

    public draw(ctx: CanvasRenderingContext2D, ignoreCache?: boolean) {
        if (this.isFocused) {
            // Don't draw while the `TextField` is focused (as a transparent input/textarea is shown above)
            return false;
        }

        // returns true if drawn from cache
        if (super.draw(ctx, ignoreCache)) {
            return true; // draw handled
        }
        // set text to empty will clear cache, thus we don't draw
        if (!this.cacheCanvas && this.text === "") {
            return true;
        }

        if (this.filters && this.filters.length) {
            this.glow = [];
            this.dropShadow = [];
            for (let j = 0; j < this.filters.length; j++) {
                let f = this.filters[j];
                if (f instanceof createjs.GlowFilter) {
                    this.glow.push({ color : ColorTransform.rgbToHex((f as any)._red, (f as any)._green, (f as any)._blue, true), width: f.strength / 10, blur: f.blurX, isScaled: f.isScaled});
                } else if (f instanceof createjs.DropShadowFilter) {
                    this.dropShadow.push({
                        // we use strength as alpha if it's between 0 and 1, since alpha doesn't really reflects strength, we compensate it to larger then 0.5
                        color : `rgba(${(f as any)._red}, ${(f as any)._green}, ${(f as any)._blue}, ${f.strength >= 1 ? 1 : f.strength >= 0.75 ? f.strength : (0.5 + f.strength / 3).toFixed(2)})`,
                        blur: (f.blurX + 1) >> 0,
                        offsetX: (f as any)._offsetX,
                        offsetY: (f as any)._offsetY
                    });
                }
            }
        }

        this.redrawBackground(ctx);
        this.drawHTMLText(ctx, {}, undefined);

        return true;
    }

    /**
     * Returns the underlying HTMLElement if available
     *
     * Only known use case is `InputTextFieldService`, which needs to work with the element itself to add TAB handling
     */
    public getTextFieldHTMLElement(): HTMLInputElement | HTMLTextAreaElement | null {
        return this._htmlTextFieldElement;
    }

    /**
     * Whenever invalidate is called, the state of this instance is set to "dirty" so that it will be redrawn in the
     * next render-step (the next "tick" event).
     */
    private invalidate(reason: string, keepLayout = false): void {
        if (!keepLayout) {
            this._INTERNAL_currentLayout = undefined;

            if (reason === `x property changed` || reason === `width property changed`) {
                // force to generate a new layot, since previousX and previousWidth need to be updated
                this._INTERNAL_previousLayout = undefined;
            }
        }
        this.uncache();

        // If textfield has filters we need to redraw immediately, otherwise it will jump around.
        if (this.filters && this.filters.length > 0) {
            this.redraw();
        } else if (!this.needsRepaint) { // only add tick if not already pending
            createjs.Ticker.addEventListener("tick", this.validate);
            this.needsRepaint = true;
            this.invalidationReason = reason;
        }
    }

    // only needed/enabled for debugging unnecessary text redraws
    private invalidationReason: string | null;

    /**
     * Called by the "tick" rendering event. Resets the state to "not dirty" and then redraws this instance
     */
    private validate = (): void => {
        if (this.stage) {
            if (this.needsRepaint) {
                // console.info(`redrawing text field "${this._textValue.text}" due to "${this.invalidationReason}"`);

                createjs.Ticker.removeEventListener("tick", this.validate);

                this.redraw();

                this.invalidationReason = null;
                this.needsRepaint = false;
            }
        } else {
            this.invalidationReason = null;
            this.needsRepaint = false;
            createjs.Ticker.removeEventListener("tick", this.validate);
        }
    }

    /**
     * Redrawing method to update positions, hitArea, the cache etc.
     */
    // TODO @HTML5 shouldnt we maybe just set the props on textJs directly without redrawing everything ?  and/or use the cache from createjs /easeljs?
    private redraw(): void {
        if (this.mouseEnabled && !this.hitArea) {
            // set a hitArea if none is present
            const hitAreaShape = new Shape();
            hitAreaShape.graphics.beginFill("#ff0000").drawRect(0, 0, this.width, this.height);
            this.hitArea = hitAreaShape;
        }

        let cacheScale:number = this.defaultCacheScale;
        if(this.stage && this.stage.scaleX > 1) {
            cacheScale = Math.max(cacheScale, 2);
        }
        if (this.width && this.height) { // observations have shown that `width`/`height` can be 0 or null in practice. Caching (and hence drawing) in such a case causes an InvalidStateError to be thrown in Firefox
            this.cache(0, 0, this.width, this.height, cacheScale); // causes draw, cache with 2x scale to fix blurry textfields insid scaled containers
        }
    }

    // Only used/needed for debugging wrongly set `mouseEnabled` values
    // private _mouseEnabled: boolean = false;
    // public get mouseEnabled(): boolean {
    //     return this._mouseEnabled;
    // }
    // public set mouseEnabled(v: boolean) {
    //     if (this.type === TextFieldType.INPUT && !v) {
    //         logger.error(`Attempting to set mouseEnabled to false for input field. Not allowed.`);
    //         return;
    //     }
    //     this._mouseEnabled = v;
    // }

    /**
     * Redraws the backround and border that will be visible behind the text if needed.
     */
    private redrawBackground(ctx: CanvasRenderingContext2D) {
        if (this._background) {
            // this.backgroundShape.alpha = 0.8; // why only 80% is this only used for debugging?

            ctx.fillStyle = this._backgroundColor;
            ctx.fillRect(0, 0, this.width, this.height);
        }
    }

    /**
     * Additional functionality - set vertical text orientation
     * Options: "top", "hanging", "middle", "alphabetic", "ideographic", "bottom"
     * Check https://html.spec.whatwg.org/multipage/scripting.html#text-styles for details
     * @returns {string}
     */
    public get textBaseline(): string {
        logger.debug(`TextField: get textBaseline not implemented, will return "top"`);
        return "top";
    }

    public set textBaseline(value: string) {
        if (value !== "top") {
            logger.error(`TextField: Only textBaseline "top" is supported`);
        }
    }

    public get capitalize(): boolean {
        return this._capitalize;
    }

    public set capitalize( allUppercase:boolean ) {
        this._capitalize = allUppercase;

        if( allUppercase ) {
            this.text = this.text.toLocaleUpperCase();
        }
    }

    /**
     * Registers all the event listeners needed to make this instance act like a "real" input textfield
     * Mouse, keyboard, input change, and focus / blur events as well as the caret animation "tick" event
     */
    private _mouseHandlersAttached = false;
    private setupListenersForInputSupport() {
        if (this._mouseHandlersAttached) {
            return;
        }

        this.addEventListener("mousedown", this.onMouseDown);
        this.addEventListener("rollover", this.onRollOver);
        this.addEventListener("pressmove", this.onPressMove);
        if ( window ) {
            window.addEventListener("resize", this.onStageResized, false);
        }

        this._mouseHandlersAttached = true;
    }

    private removeMouseInputHandlers() {
        this.removeEventListener("mousedown", this.onMouseDown);
        this.removeEventListener("rollover", this.onRollOver);
        this.removeEventListener("pressmove", this.onPressMove);
        if ( window ) {
            window.removeEventListener("resize", this.onStageResized);
        }

        this._mouseHandlersAttached = false;
    }

    /**
     * Event handler for the mouse down event
     * @param e
     */
    private onMouseDown = (e: any) => {
        if (!this.isInputTextField() && this._selectable) {
            const mousePoint = this.normalizeCoordinates(e);

            if (mousePoint) {
                const position = this.caretCharacterIndexAtElementPosition(mousePoint);

                let positionChanged: boolean = this.lastMouseDownPos !== position;

                this._selectionBeginIndex = position;
                this._selectionEndIndex = position;
                this.lastMouseDownPos = position;

                this.handleDoubleClick(Number(position) >>> 0, positionChanged);

                this.invalidate(`selection changed`, true);
            }
        }

        if (!this.isInputTextField()) {
            return;
        }

        const mouseDownPoint = this.normalizeCoordinates(e);
        if (!mouseDownPoint) {
            return;
        }

        this.mouseDownPoint = mouseDownPoint;
        if (this._htmlTextFieldElement) {
            this.onFocus();
            const position = this.caretCharacterIndexAtElementPosition(mouseDownPoint);
            if (position !== undefined) {
                this._htmlTextFieldElement.setSelectionRange(position as number, position as number);
            }
        } else {
            logger.error(`TextField clicked, but not _htmlInputElement set`, this.name);
        }
    }

    /**
     * Event handler for the roll over event
     * @param e
     */
    private onRollOver = (e: any) => {
        if (this.isInputTextField() || this._selectable) {
            Mouse.instance().showCssCursor(CSSMouseCursor.TEXT);
        }
    }

    private onStageResized = (e: any) => {
        if ( this.isInputTextField() ) {
            this.blur();
        }
    }

    private onPressMove = (e: any) => {
        if (this._selectable && this.lastMouseDownPos !== undefined && !this.isInputTextField()) {
            const mousePoint = this.normalizeCoordinates(e);

            if (mousePoint) {
                const position = this.caretCharacterIndexAtElementPosition(mousePoint);

                if (position !== undefined && position < this.lastMouseDownPos) {
                    this._selectionBeginIndex = position;
                    this._selectionEndIndex = this.lastMouseDownPos;
                } else if (position !== undefined && position > this.lastMouseDownPos) {
                    this._selectionBeginIndex = this.lastMouseDownPos;
                    this._selectionEndIndex = position;
                }

                this.updateSelectedText();

                this.invalidate(`selection changed`, true);
            }
        }
    }

    private updateSelectedText() {
        if (this._selectionBeginIndex !== undefined && this.selectionEndIndex !== undefined) {
            const charactersByToken: string[][] = this.latestLayout.drawingTokens.filter(t => t.type === "plain").map(t => characters((t as any).text));
            const allCharacters = flatten(charactersByToken);
            selectionText = allCharacters.slice(this._selectionBeginIndex, this._selectionEndIndex).join(``);
        } else {
            selectionText = undefined;
        }
    }

    private caretCharacterIndexAtElementPosition(cursorPoint: { x: number, y: number }): number | undefined {
        const layout = this.latestLayout;

        let drawingStartX = 0;
        let drawingStartXLine = 0;
        let drawingStartY = 0;
        let printedCharacters = 0;
        let printedCharactersLineStart = 0;
        let format = this.textFormat;

        // helper function to find the correct position if the cursor is not directly on a character.
        // we want to select te start/end of the line in this case.
        let findOutOfTextfieldPos: Function = (): number => {
            if (cursorPoint.y < drawingStartY || (cursorPoint.x < drawingStartXLine && cursorPoint.y >= drawingStartY && cursorPoint.y < drawingStartY + layout.metrics.lineHeight)) {
                return printedCharactersLineStart;
            } else if ((cursorPoint.y <= drawingStartY + layout.metrics.lineHeight) && (cursorPoint.x >= drawingStartX)) {
                return printedCharacters;
            }
            return -1;
        };

        for (const token of layout.drawingTokens) {
            switch (token.type) {
                case "plain":
                    if (
                        cursorPoint.x >= drawingStartX &&
                        cursorPoint.x <= drawingStartX + token.width &&
                        cursorPoint.y >= drawingStartY &&
                        cursorPoint.y <= drawingStartY + layout.metrics.lineHeight
                    ) {
                        // caret is inside this token

                        const ctx = (Text as any)._workingContext;
                        applyFormat(ctx, format, TextField.FontRestriction);

                        let text = "";
                        let previousDistance: number = drawingStartX;
                        let actualDistance: number = drawingStartX;
                        const textCharacters = characters(token.text);
                        for (const [i, character] of Array.from(textCharacters.entries())) {
                            previousDistance = actualDistance;

                            text += character;
                            actualDistance = drawingStartX + ctx.measureText(text).width;

                            if (actualDistance > cursorPoint.x) {
                                if (cursorPoint.x - previousDistance > actualDistance - cursorPoint.x) {
                                    return printedCharacters + i + 1;
                                } else {
                                    return printedCharacters + i;
                                }
                            }
                        }

                        logger.error(`did not find character boundary for cursor, even though it should be inside this token`, token.text, textCharacters);
                    }
                    drawingStartX += token.width;
                    printedCharacters += characters(token.text).length;

                    if ( layout.drawingTokens.indexOf(token) === layout.drawingTokens.length - 1) {
                        let outOfTextfieldPos: number = findOutOfTextfieldPos();
                        if (outOfTextfieldPos > -1) {
                            return outOfTextfieldPos;
                        }
                    }
                    break;
                case "set-x-y":
                    if (printedCharacters > 0) {
                        let outOfTextfieldPos: number = findOutOfTextfieldPos();
                        if (outOfTextfieldPos > -1) {
                            return outOfTextfieldPos;
                        }
                    }

                    drawingStartX = token.x;
                    drawingStartXLine = token.x;
                    drawingStartY = token.y;
                    printedCharactersLineStart = printedCharacters;
                    break;
                case "advance-x":
                    drawingStartX += token.amount;
                    break;
                case "switch-to-format":
                    format = token.format;
                    break;
            }
        }
        if (cursorPoint.y > drawingStartY + layout.metrics.lineHeight) {
            return printedCharacters;
        }

        // logger.info(`did not find index at point`, cursorPoint.x, cursorPoint.y);
        return undefined;
    }

    /**
     * Whenever the user inputs text into the (hidden) <input> element, this event will be called
     */
    private onInput = (e: KeyboardEvent) => {
        if (this.isFocused && this._htmlTextFieldElement) {
            e.preventDefault();
            this.validateRestrict();
            this.text = this._htmlTextFieldElement.value; // will invalidate text already

            if (this._htmlTextFieldElement && this.isFocused) {
                let value: number = Math.min(this.maxScrollV, Math.max(Math.ceil(this._htmlTextFieldElement.scrollTop / this.latestLayout.metrics.lineHeight) + 1, 1));
                this.scrollV = value;
            }

            this.invalidate(`onInput / text changed`); // TODO: Remove
            this.dispatchEvent(new createjs.Event(createjs.Event.CHANGE, true, false));
        }
    }

    /**
     * Whenever the user creates a key down event by pressing a key, this handler will be called.
     * It is used to propagate the event to listeners in parents or parents of parents of this textfield ( e.g. a
     * event handler in a dialog that wants to listen to the "ENTER" key event to "submit" some data etc.)
     * @param e
     */
    private onKeyDown = (e: KeyboardEvent) => {
        if (this.isFocused) {
            const convertedEvent = KeyboardEventDispatcher.convertEvent(e);
            this.dispatchEvent(convertedEvent);

            if (e.ctrlKey || (e.keyCode >= 35 && e.keyCode <= 40)) {
                this.invalidate(`onKeyDown`);
            }
        }
    }

    /**
     * Whenever the user creates a key up event by releasing a key, this handler will be called.
     * It is used to propagate the event to listeners in parents or parents of parents of this textfield ( e.g. a
     * event handler in a dialog that wants to listen to the "ENTER" key event to "submit" some data etc.)
     * @param e
     */
    private onKeyUp = (e: KeyboardEvent) => {
        if (this.isFocused) {
            const convertedEvent = KeyboardEventDispatcher.convertEvent(e);
            this.dispatchEvent(convertedEvent);
        }
    }

    /**
     * Helper method to find out if the type of this instance is set to TextFieldType.INPUT
     * @returns {boolean}
     */
    private isInputTextField(): boolean {
        return this.type === TextFieldType.INPUT;
    }

    /**
     * Used to validate the restriction regex
     */
    private validateRestrict(): void {
        if (this._restrict && this._htmlTextFieldElement) {
            if (this._htmlTextFieldElement.value.length > 0) {
                this._htmlTextFieldElement.value = this._htmlTextFieldElement.value.replace(this._restrict, "");
            }
        }
    }

    /**
     * Handler method that will be called whenever this input text instance got the focus
     */
    private onFocus(): void {
        if (!this._htmlTextFieldElement) {
            return;
        }

        if (this.isFocused) {
            return; // nothing to do
        }

        this.dispatchEvent(FocusEvent.FOCUS_IN);

        let globalScale: number = this.scaleY;
        let parent: Container | null = this.parent;

        while (parent && !(parent instanceof Stage)) {
            globalScale *= parent.scaleY;
            parent = parent.parent;
        }

        this._htmlTextFieldElement.style.display = "block";
        this._htmlTextFieldElement.style.font = this.textFormat.fontType;

        if (!this.isDefaultText) {
            this._htmlTextFieldElement.value = this.text;
        }

        let dpiScale:number = 1;
        if (TextField.scaleHTMLByPixelDensity) {
            let dpi:number = window.devicePixelRatio || 1;
            dpiScale = 1 / dpi;
        }
        // For some reason the position is not always aligned with the canvas rendering, and individual fields require a different `y` delta
        // As text fields are already misaligned (too high up) on the canvas, I deferred getting these placed correctly until all underlying issues are resolved
        const pos = this.localToGlobal(2, (this.multiline ? 2 : 0));
        this._htmlTextFieldElement.style.top = `${Math.floor(pos.y * dpiScale)}px`; // Rounding and flooring the value, so its drawing aligns with the canvas text
        this._htmlTextFieldElement.style.left = `${Math.round(pos.x * dpiScale)}px`;
        this._htmlTextFieldElement.style.width = `${(this.width - 2) * globalScale}px`;
        this._htmlTextFieldElement.style.height = `${(this.height - (this.multiline ? 2 : 0)) * globalScale}px`;
        this._htmlTextFieldElement.style.color = this.textColor as string;
        this._htmlTextFieldElement.style.margin = `0`;
        this._htmlTextFieldElement.style.padding = `0`;
        this._htmlTextFieldElement.style.fontFamily = getFontFamily(this.textFormat, TextField.FontRestriction);
        this._htmlTextFieldElement.style.fontSize = `${(this.textFormat.size || FALLBACK_TEXT_SIZE) * globalScale}px`;
        this._htmlTextFieldElement.style.lineHeight = `${this.latestLayout.metrics.lineHeight * globalScale}px`;

        this._htmlTextFieldElement.focus();
        this._htmlTextFieldElement.scrollTop = (this._firstVisibleLineIndex - 1) * this.latestLayout.metrics.lineHeight;

        this._htmlTextFieldElement.style.transform = "scale(" + dpiScale + ")";
        this._htmlTextFieldElement.style.transformOrigin = "left top";

        this.updateTextFieldVisibleInterval();
    }

    /**
     * Handler method that will be called whenever this input text instance lost the focus
     */
    private onBlur(): void {
        this.deSelectHtmlInput();

        this.updateTextFieldVisibleInterval();

        this.isDefaultText = this.text === "";
    }

    /**
     * Check hit test for TextField area
     * @param localXY
     * @returns {boolean}
     */
    private isPointWithinHitArea(localXY: Point): boolean {
        if (localXY) {
            return localXY.x > 0 && localXY.y > 0 && ((localXY.x < this._width && localXY.y < this._height) || (localXY.x < this._width && localXY.y < this._height));
        } else {
            return false;
        }
    }

    /**
     * Used internally to normalize the mouse coordinates (TextField.mouseDownPoint, TextField.mouseUpPoint) for later use
     */
    private normalizeCoordinates(e: any): Point | undefined {
        if (this.stage === null) {
            return undefined;
        }

        // Page
        const x = e.pageX !== undefined ? e.pageX : e.stageX;
        const y = e.pageY !== undefined ? e.pageY : e.stageY;

        return this.globalToLocal(x, y);
    }

    /**
     * Used internally to remove event listeners and clean up the dom if this instance was a of type "input"
     */
    private teardown() {
        if (this._htmlTextFieldElement && this._htmlTextFieldElement.parentElement === document.body) {
            this._htmlTextFieldElement.removeEventListener("input", this.onInput);
            this._htmlTextFieldElement.removeEventListener("keydown", this.onKeyDown);
            this._htmlTextFieldElement.removeEventListener("keyup", this.onKeyUp);

            document.body.removeChild(this._htmlTextFieldElement);
            this._htmlTextFieldElement = null;
        } else if (this._htmlTextFieldElement) {
            logger.error(`has this._htmlInputElement but on different node`);
        }

        this.removeMouseInputHandlers();

        this.inputTextFieldService.unregisterInputField(this);

        if (this._textFieldVisibleIntervalId) {
            window.clearInterval(this._textFieldVisibleIntervalId);
            this._textFieldVisibleIntervalId = undefined;
        }
    }

    /**
     * * Called by the onBlur (focusLost) event handler
     */
    private deSelectHtmlInput() {
        if (!this._htmlTextFieldElement) {
            return;
        }

        this._htmlTextFieldElement.style.display = "none";
        this._htmlTextFieldElement.blur();
    }

    /**
     * Focus this `TextField`, if it allows input
     *
     * Will highlight all text, as this is the common behavior in forms
     */
    public focus() {
        if (this.isInputTextField() && this._htmlTextFieldElement) {
            this.onFocus();

            this._htmlTextFieldElement.select();
        }
    }

    public blur() {
        if (this._htmlTextFieldElement && this.isFocused) {
            this._htmlTextFieldElement.blur();
        }
    }

    private handleDoubleClick(pos: number, positionChanged: boolean) {
        const doubleClickTime: number = 500;
        let now: number = Date.now();
        if ((now - this.lastMouseDownTime < doubleClickTime) && this.consecutiveMouseClicks < 3 && !positionChanged) {
            this.consecutiveMouseClicks++;
        } else {
            this.consecutiveMouseClicks = 1;
        }
        if (this.consecutiveMouseClicks > 1) {
            let left: number = pos;
            let right: number = pos;

            const charactersByToken: string[][] = this.latestLayout.drawingTokens.filter(t => t.type === "plain").map(t => characters((t as any).text));
            const flattenedText: string[] = flatten(charactersByToken);
            if (this.consecutiveMouseClicks === 2) {
                left = flattenedText.slice(0, left + 1).join(``).search(/(\w+$)|(\W$)/);
                right = flattenedText.slice(right).join(``).search(/\W|$/);
            } else if (this.consecutiveMouseClicks === 3) {
                left =  flattenedText.slice(0, left + 1).join(``).search(/[^\n]+$/);
                right = flattenedText.slice(right).join(``).search(/\n|$/);
            }
            this._selectionBeginIndex = left;
            this._selectionEndIndex = Math.max(pos + right, left + 1);
        }

        this.lastMouseDownTime = now;
    }
}

function parsePlainText(input: string): RichTextInputToken[] {
    return input.split(/(\n)/).map((chunk): RichTextInputToken => {
        if (chunk === `\n`) {
            return { type: "newline" };
        } else {
            return { type: "plain", text: chunk };
        }
    });
}

function parseHTMLText(input: string): RichTextInputToken[] {
    /**
     * Input already has replaced placeholders, so it might contain text like
     * `[color=#499100][12:17]   user5: [/color]Hello!`
     *
     * Therefore we only accept BBCode where the tag starts with a letter (e.g. `[color]`),
     * and not a number, as the notation `[12:17]` is used for timestamps
     *
     * If we ever needed letters surrounded by `[]` in the output,
     * we would need to update `(Localized)TextVO` to provide us with escaped brackets (for example HTML chracter references)
     */
    const chunks = input.split(/(\[\/?[a-zA-Z][^\]]+]|<[^<>]+[<>]|\n)/);
    const parts: RichTextInputToken[] = [];

    for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex += 2) {
        const chunk = chunks[chunkIndex];
        const control = chunks[chunkIndex + 1];

        parts.push({ type: "plain", text: chunk });

        if (control) {
            if (control === "\n" || control === "<br>" || control === "<br/>") {
                parts.push({ type: "newline" });
            } else {
                if (control.endsWith("<")) {
                    // BigFarm style link

                    const search = /<(\d+)([^<]+)</.exec(control);

                    if (search) {
                        const linkId = search[1];
                        const linkText = search[2];

                        parts.push({ type: "start-link", linkId: linkId });
                        parts.push({ type: "plain", text: linkText });
                        parts.push({ type: "end-link" });
                    } else {
                        logger.error(`skipped tag part`, control);
                    }
                } else {
                    // BBCode or HTML

                    if (control[1] === "/") {
                        // closing tag like [/color]

                        if (control === "[/url]") {
                            parts.push({ type: "end-link" });
                        } else if (control === "[/color]" || control === "[/font]") {
                            parts.push({ type: "pop-last-format-augmentation" });
                        } else if (control === "</a>") {
                            parts.push({ type: "end-link" });
                        }
                        // else: nothing, for example a `</p>`
                    } else {
                        // opening tag

                        const pattern = /(\w+)(?:=((?:"[^"]*")|(?:'[^']*')|(?:\S+)))?/g;
                        const stringValue = control.slice(1, -1);

                        let search: RegExpExecArray | null;

                        const attributes: [string, string][] = [];

                        while ((search = pattern.exec(stringValue))) {
                            const attributeName = search[1];
                            let attributeValue = search[2];

                            if (attributeValue && (attributeValue[0] === "'" || attributeValue[0] === '"')) {
                                attributeValue = attributeValue.slice(1, -1);
                            }

                            attributes.push([attributeName, attributeValue]);
                        }
                        const tagName = attributes.length > 0 ? attributes[0][0] : "";
                        switch (tagName) {
                            case "font": {
                                const newFormat: { [key: string]: any } = {};
                                for (let attrI = 1, attrLength = attributes.length; attrI < attrLength; attrI++) {
                                    newFormat[attributes[attrI][0]] = attributes[attrI][1];
                                }
                                parts.push({ type: "push-format-augmentation", partialFormat: newFormat });
                                break;
                            }
                            case "url": {
                                const link = attributes[0][1];
                                parts.push({ type: "start-link", linkId: link });
                                break;
                            }
                            case "b":
                                parts.push({ type: "push-format-augmentation", partialFormat: { bold: true } });
                                break;
                            case "color":
                                parts.push({ type: "push-format-augmentation", partialFormat: { color: attributes[0][1] } });
                                break;
                            case "a":
                                let anchorUrl = "";
                                for (let i = 1, attrLength = attributes.length; i < attrLength; i++) {
                                    if (attributes[i][0] === "href") {
                                        anchorUrl = attributes[i][1];
                                        break;
                                    }
                                }
                                parts.push({ type: "start-link", linkId: anchorUrl , isAnchorLink: true});
                                break;
                            case "content":
                                break;
                            default:
                                logger.error(`parseHTML: Could not match tag name`, tagName, attributes);
                                break;
                        }
                    }
                }
            }
        }
    }

    return parts;
}

function setXMoveY(state: TextDrawingState, x: number, y: number): TextDrawingState {
    if (state.currentLink) {
        state.currentLink.link.endCurrentLinkRect(state.xOffset, state.yOffset + state.lineHeight);
        state.currentLink.link.startLinkRect(x, y);
    }

    return {
        ...state,
        xOffset: x,
        yOffset: y,
    };
}

/**
 * Get all characters of a string with unicode characters.
 *
 * It has the same effect of <code>return s.split(/(?!$)/u)</code>, but is compatible with ES5/old browsers.
 *
 * @param s string string with unicode characters
 *
 * @link https://mathiasbynens.be/notes/javascript-unicode#iterating-over-symbols
 */
function characters(s: string): string[] {
    let index: number = 0;
    let length: number = s.length;
    let output: string[] = [];

    for (; index < length - 1; ++index) {
        let charCode: number = s.charCodeAt(index);

        if (charCode >= 0xD800 && charCode <= 0xDBFF) {
            charCode = s.charCodeAt(index + 1);

            if (charCode >= 0xDC00 && charCode <= 0xDFFF) {
                output.push(s.slice(index, index + 2));
                ++index;
                continue;
            }
        }

        output.push(s.charAt(index));
    }

    output.push(s.charAt(index));

    return output;
}

function drawText(ctx: CanvasRenderingContext2D, state: TextDrawingState, text: string, chunkWidth: number, viewHeight: number): TextDrawingState {
    if (state.yOffset < -state.lineHeight || state.yOffset > viewHeight) {
        // skip drawing text fully outside of the bounds of the view
        return {
            ...state,
            xOffset: state.xOffset + chunkWidth,
            printedCharacters: state.printedCharacters + characters(text).length,
        };
    }

    for (let d = state.dropShadow.length - 1; d >= 0; d--) {
        let dropShadow: ShadowType = state.dropShadow[d];
        ctx.save();
        ctx.shadowColor = dropShadow.color; // glow colour
        ctx.shadowBlur = dropShadow.blur;
        ctx.shadowOffsetX = dropShadow.offsetX;
        ctx.shadowOffsetY = dropShadow.offsetY;
        ctx.fillStyle = dropShadow.color;
        ctx.fillText(text, state.xOffset, state.yOffset + state.distanceToBaseline); // glow
        if (!dropShadow.offsetX && !dropShadow.offsetY) {
            ctx.shadowBlur = 0;
            ctx.lineWidth = 4 * (d + 1);
            ctx.strokeStyle  = dropShadow.color;
            ctx.strokeText(text, state.xOffset, state.yOffset + state.distanceToBaseline); // glow
        }
        ctx.restore();
    }

    for (let g = state.glow.length - 1; g >= 0; g--) {
        let glow: GlowType = state.glow[g];
        ctx.save();
        ctx.strokeStyle  = glow.color; // glow colour
        ctx.lineWidth = glow.blur / (glow.isScaled ? 2 : 1); // devided by defaultCacheScale if scaled
        if ( ctx.lineWidth === 2 ) {
            ctx.lineWidth = 3;
        }
        if (state.currentFormat.size / ctx.lineWidth <= 3) {
            ctx.lineWidth /= 2;
            ctx.strokeText(text, state.xOffset + 1, state.yOffset + state.distanceToBaseline);
            ctx.strokeText(text, state.xOffset - 1, state.yOffset + state.distanceToBaseline);
            ctx.strokeText(text, state.xOffset, state.yOffset + state.distanceToBaseline + 1);
            ctx.strokeText(text, state.xOffset, state.yOffset + state.distanceToBaseline - 1);
        } else {
            ctx.strokeText(text, state.xOffset, state.yOffset + state.distanceToBaseline); // glow
        }
        ctx.restore();
    }

    let textCharacters: string[] | undefined;
    if (state.selection) {
        textCharacters = characters(text);
        if (state.printedCharacters < state.selection.end && state.printedCharacters + textCharacters.length > state.selection.start) {
            const previousFillColor = ctx.fillStyle;

            const relativeStart = Math.max(0, state.selection.start - state.printedCharacters);
            const selectionLength = state.selection.end - state.selection.start;
            const relativeEnd = state.selection.start - state.printedCharacters + selectionLength;

            const textBeforeHighlight = textCharacters.slice(0, relativeStart).join(``);
            const highlightedText = textCharacters.slice(relativeStart, relativeEnd).join(``);

            const distanceBefore = ctx.measureText(textBeforeHighlight).width;
            const highlightLength = ctx.measureText(highlightedText).width;

            ctx.fillStyle = `lightblue`;
            ctx.fillRect(state.xOffset + distanceBefore, state.yOffset, highlightLength, state.lineHeight);

            ctx.fillStyle = previousFillColor;
        }
    }

    ctx.fillText(text, state.xOffset, state.yOffset + state.distanceToBaseline); // don't add `maxWidth`, as that can shrink kerning/font-size

    if (state.currentFormat.underline) {
        underline(ctx, state, chunkWidth);
    }

    return {
        ...state,
        xOffset: state.xOffset + chunkWidth,
        printedCharacters: state.printedCharacters + (textCharacters ? textCharacters.length : 0),
    };
}

function applyFormatToState(state: TextDrawingState, format: TextFormat): TextDrawingState {
    return {
        ...state,
        currentFormat: format,
    };
}

function startLink(state: TextDrawingState, linkId: string, hoveredLinkIndex: number, isAnchorLink = false): TextDrawingState {
    let color: string;
    let hasUnderLine = true;
    if (hoveredLinkIndex !== -1 && hoveredLinkIndex === state.closedLinks.length) {
        // checks wheter this link (the next one), is currently hovered
        if (state.htmlLinkFormats && state.htmlLinkFormats.hover) {
            color = state.htmlLinkFormats.hover.color;
            hasUnderLine = state.htmlLinkFormats.hover.underline || hasUnderLine;
        } else {
            color = typeof state.currentFormat.color === "string" ? state.currentFormat.color : "#ffffff";
        }
    } else {
        if (state.htmlLinkFormats && state.htmlLinkFormats.normal) {
            color = state.htmlLinkFormats.normal.color;
            hasUnderLine = state.htmlLinkFormats.normal.underline || hasUnderLine;
        } else if (state.currentFormat.color === "#ffffff" || state.currentFormat.color === "#FFFFFF") {
            color = WHITE_TEXT_PASSIVE_LINK_COLOR;
        } else if (state.currentFormat.color === "#000000" || state.currentFormat.color === "#262014" /* dark gray used for text */) {
            color = DARK_TEXT_PASSIVE_LINK_COLOR;
        } else {
            color = DEFAULT_PASSIVE_LINK_COLOR;
        }
    }

    const previousFormat = state.currentFormat;
    const newFormat: TextFormat = { ...state.currentFormat, color: color, underline: hasUnderLine };

    const link = new Link(linkId, isAnchorLink);
    link.startLinkRect(state.xOffset, state.yOffset);
    state = {
        ...state,
        currentLink: {
            link: link,
            previousFormat: previousFormat,
        },
        currentFormat: newFormat,
    };

    return state;
}

function finalizeLink(state: TextDrawingState): TextDrawingState {
    if (state.currentLink) {
        state.currentLink.link.endCurrentLinkRect(state.xOffset, state.yOffset + state.lineHeight);

        state = {
            ...state,
            currentLink: null,
            currentFormat: state.currentLink.previousFormat,
            closedLinks: [...state.closedLinks, state.currentLink.link],
        };

        return state;
    } else {
        throw new Error("Closing link tag found, but no opening tag");
    }
}

function underline(ctx: CanvasRenderingContext2D, state: TextDrawingState, width: number) {
    ctx.strokeStyle = typeof state.currentFormat.color === "string" ? state.currentFormat.color : FALLBACK_UNDERLINE_COLOR; // TODO: @HTML5 handle number values
    ctx.beginPath();
    ctx.moveTo(state.xOffset, state.yOffset + state.distanceToBaseline + state.underLinePlacement);
    ctx.lineTo(state.xOffset + width, state.yOffset + state.distanceToBaseline + state.underLinePlacement);
    ctx.stroke();
}
